/**
 * Mutual exclusion for concurrent callers of the same keyed task: whoever asks first runs it, and
 * anyone asking while it is still in flight gets that same run instead of starting their own.
 *
 * The key is forgotten once its run settles, so this is not a record of "already ran" — a later
 * caller starts a fresh run. Use it where the same work can be kicked off from two places at once
 * and the second one must not repeat it.
 */
export function createSharedRuns<T>() {
  const inFlight = new Map<string, Promise<T>>();

  return function sharedRun(key: string, run: () => Promise<T>): Promise<T> {
    let existing = inFlight.get(key);

    if (!existing) {
      existing = run().finally(() => inFlight.delete(key));
      inFlight.set(key, existing);
    }

    return existing;
  };
}
